// src/lib/myday.ts — My Day: shapes and calls shared by the page and the header bell.
import { api } from './api';

export type TaskState = 'open' | 'snoozed' | 'done';
export type Bucket = 'overdue' | 'today' | 'week' | 'later';
export type Team = 'maintenance' | 'lettings' | 'accounts' | 'directors' | 'resident';

export interface MyDayTask {
  key: string;
  kind: string;
  kindLabel: string;
  team: Team;
  teamLabel: string;
  title: string;
  detail: string;
  due: string | null;
  overdue: boolean;
  bucket: Bucket;
  severity: 'high' | 'normal';
  url: string | null;
  record: { resource: string; id: string; label: string };
  ownerId: string | null;
  ownerName: string | null;
  handed: { byId: string; byName: string | null; at: string; note: string | null } | null;
  state: TaskState;
  snoozedUntil: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
}

export interface MyDay {
  who: { id: string | null; name: string; all: boolean };
  show: TaskState;
  counts: { open: number; snoozed: number; done: number; overdue: number; today: number; week: number; later: number };
  byKind: Record<string, number>;
  tasks: MyDayTask[];
}

export interface BellItem {
  type: 'task' | 'notification';
  id: string;
  title: string;
  detail: string | null;
  url: string | null;
  severity: 'high' | 'normal';
  due: string | null;
  overdue: boolean;
  at: string | null;
}
export interface Bell { count: number; tasks: number; unread: number; items: BellItem[] }

export const TEAMS: Array<{ key: Team; label: string }> = [
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'lettings', label: 'Lettings' },
  { key: 'accounts', label: 'Accounts' },
  { key: 'directors', label: 'Directors' },
];

const enc = encodeURIComponent;
export const myDayApi = {
  list: async (q: { who?: string; team?: string; show?: TaskState }) => (await api.get<MyDay>('/my-day', q)).data,
  bell: async () => (await api.get<Bell>('/my-day/bell')).data,
  done: (key: string, note?: string) => api.post<MyDayTask>(`/my-day/${enc(key)}/done`, { note: note || null }),
  snooze: (key: string, until: string) => api.post<MyDayTask>(`/my-day/${enc(key)}/snooze`, { until }),
  reopen: (key: string) => api.post<MyDayTask>(`/my-day/${enc(key)}/reopen`, {}),
  handOver: (key: string, to: { userId?: string; email?: string }, note?: string) => api.post<MyDayTask>(`/my-day/${enc(key)}/hand-over`, { ...to, note: note || null }),
  markNotificationsRead: (ids?: string[]) => api.post<{ marked: number }>('/notifications/read', ids ? { ids } : {}),
};

/** Local YYYY-MM-DD n days from today. */
export function isoIn(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** Next Monday (never today). */
export function nextMonday(): string {
  const dow = new Date().getDay();             // 0 Sun … 6 Sat
  return isoIn(((8 - dow) % 7) || 7);
}

export const BUCKETS: Array<{ key: Bucket; label: string; empty: string }> = [
  { key: 'overdue', label: 'Overdue', empty: 'Nothing overdue.' },
  { key: 'today', label: 'Today', empty: 'Nothing due today.' },
  { key: 'week', label: 'This week', empty: 'Nothing else this week.' },
  { key: 'later', label: 'Later', empty: 'Nothing further out.' },
];

/** One colour per kind, for the row's left rule (mirrors the prototype's TASK_ICON). */
export const KIND_COLOUR: Record<string, string> = {
  workOrderOverdue: '#d9534f', workOrderNew: '#5b7fd1', certificate: '#d4932c', assetService: '#8fa3b8',
  deposit: '#8b6fd6', tenancyEnding: '#3fa7b5', viewing: '#3aa877', viewingFeedback: '#3aa877',
  arrears: '#d9534f', billUnpaid: '#a07a4f', billSignoff: '#8b6fd6', aiAction: '#8b6fd6',
  repairOnHold: '#d4932c', rentDue: '#a07a4f', messageUnanswered: '#5b9dff',
  checklistDue: '#a78bfa', closingEmail: '#3ddc97',
  contractNotice: '#b5832f', section20: '#d9534f', buildingSection: '#d4932c', manual: '#a88662',
  outboxUnsent: '#5b9dff', bankUnallocated: '#a07a4f', workNoInvoice: '#b5832f', contractorInsurance: '#ff8f8f',
  vacantNotAdvertised: '#3aa877', vacatingNotAdvertised: '#3fa7b5', messageTeamAdded: '#7f8cf8',
};
